import React, { ReactNode } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { Pencil } from "lucide-react";
import {
  ConsolidatedCoinData,
  LanguageNameSpaces,
  PositionData,
  PrecisionType,
} from "@shared/types";
import {
  CONTRACTS_EXCHANGES,
  ICON_TYPES,
  MARGIN_TYPE_TO_DISPLAY,
} from "@shared/common/constants.ts";
import { selectDisplayCurrency } from "@shared/slices/common.slice.ts";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions.ts";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc.ts";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers.ts";
import { getAssetFromSymbol } from "@shared/utils/misc.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import IconWithFallback from "Global/IconWithFallback.tsx";
import LiveSymbolPrice from "common/LiveComponents/LiveSymbolPrice.tsx";
import { DISPLAY_CURRENCIES } from "common/constants.ts";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { formatSignedPercent } from "features/portfolio/NewLook/format.ts";
import { getPositionPnlBase } from "features/portfolio/NewLook/positionFigures.ts";

type PositionItem = PositionData | ConsolidatedCoinData;

const exchangeOf = (data: PositionItem) =>
  "exchange" in data ? data.exchange : data.exchanges?.[0];

/**
 * The positions table's cells in the new look: each a figure over a meta
 * line, numbers tabular, P&L in the direction's colour (no pills), the
 * liquidation price in the stop colour.
 */
const useNewLookPositionCells = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const tokens = getNewLookTokens(useTheme());
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isBtc = displayCurrency === DISPLAY_CURRENCIES.BTC;
  const { getPrecision, applyPrecision } = usePrecisions();
  const { getMainCoin, getDisplayCoin } = useSymbolsMetadata();

  const amount = (value: number, data: PositionItem) =>
    getLocaleStringWithPrecision(
      applyPrecision(value, PrecisionType.AMOUNT, data.symbol, exchangeOf(data))
    );
  const price = (value: number, data: PositionItem) =>
    getLocaleStringWithPrecision(
      value,
      getPrecision(PrecisionType.PRICE, data.symbol, exchangeOf(data))
    );
  const na = <Muted>{t("common.n_a")}</Muted>;

  const symbol = (data: PositionItem, account?: string) => (
    <Identity>
      <IconWithFallback
        iconType={ICON_TYPES.COIN}
        coinOrExchangeName={data.symbol}
        pxIconSize={28}
      />
      <Stack>
        <Name>{data.symbol}</Name>
        {account && <Meta title={account}>{account}</Meta>}
      </Stack>
    </Identity>
  );

  const direction = (data: PositionItem, onEdit?: () => void) => (
    <Stack>
      <Side style={{ color: data.is_short ? tokens.down : tokens.up }}>
        {data.is_short ? t("positions.short") : t("positions.long")}
      </Side>
      <Meta>
        {MARGIN_TYPE_TO_DISPLAY[data.margin_type] || ""}
        {data.leverage ? ` ${data.leverage}x` : ""}
        {onEdit && (
          <EditButton
            type="button"
            aria-label={t("buttons.update_leverage")}
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
          >
            <Pencil size={12} strokeWidth={NEW_LOOK_ICON_STROKE} />
          </EditButton>
        )}
      </Meta>
    </Stack>
  );

  const size = (data: PositionItem, end = true) => {
    if (!data.position_size) return na;
    const exchange = exchangeOf(data) || "";
    return (
      <Stack $end={end}>
        <Figure>
          {amount(data.position_size, data)}
          <Unit>{getDisplayCoin(data.symbol, exchange)}</Unit>
        </Figure>
        <Meta>
          {getLocaleStringWithPrecision(
            data.quantity ?? 0,
            getPrecision(PrecisionType.QUANTITY, data.symbol, exchange),
            true
          )}
          <Unit>
            {CONTRACTS_EXCHANGES.has(exchange)
              ? t("positions.cont")
              : getAssetFromSymbol(data.symbol)}
          </Unit>
        </Meta>
      </Stack>
    );
  };

  const margin = (data: PositionItem, end = true) => {
    if (!data.margin) return na;
    const exchange = exchangeOf(data) || "";
    const value = data.margin[isBtc ? 1 : 2];
    const precision = getPrecision(
      isBtc ? PrecisionType.BTC_AMOUNT : PrecisionType.USD_AMOUNT,
      data.symbol,
      exchange
    );
    return (
      <Stack $end={end}>
        <Figure>
          {isBtc ? "₿" : "$"}
          {getLocaleStringWithPrecision(value, precision)}
        </Figure>
        <Meta>
          {amount(data.margin[0], data)}
          <Unit>{getMainCoin(data.symbol, exchange)}</Unit>
        </Meta>
      </Stack>
    );
  };

  const pnl = (data: PositionItem, end = true) => {
    if (!data.unrealized_pnl) return na;
    const exchange = exchangeOf(data) || "";
    const base = getPositionPnlBase(data);
    const roi = base ? (data.unrealized_pnl / base) * 100 : undefined;
    const color = directionColor(tokens, data.unrealized_pnl);
    return (
      <Stack $end={end}>
        <Figure style={{ color }}>
          {data.unrealized_pnl > 0 ? "+" : data.unrealized_pnl < 0 ? "−" : ""}
          {amount(Math.abs(data.unrealized_pnl), data)}
          <Unit>{getDisplayCoin(data.symbol, exchange)}</Unit>
        </Figure>
        {roi !== undefined && (
          <Meta style={{ color, fontWeight: 500 }}>
            {formatSignedPercent(roi)}
          </Meta>
        )}
      </Stack>
    );
  };

  const entry = (data: PositionItem, end = true) => (
    <Stack $end={end}>
      <Figure>{data.entry_price ? price(data.entry_price, data) : "—"}</Figure>
      <Meta>
        <LiveSymbolPrice
          symbol={data.symbol}
          hideIcon={true}
          exchange={exchangeOf(data)}
          initialPrice={data.price || 0}
          fontSize={12}
          textColor={tokens.muted}
        />
      </Meta>
    </Stack>
  );

  const liquidation = (data: PositionItem): ReactNode =>
    data.liquidation_price ? (
      <Figure style={{ color: tokens.stop }}>
        {price(data.liquidation_price, data)}
      </Figure>
    ) : (
      na
    );

  return { symbol, direction, size, margin, pnl, entry, liquidation };
};

export default useNewLookPositionCells;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
const Stack = styled.div<{ $end?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$end ? "flex-end" : "flex-start")};
  gap: 2px;
  min-width: 0;
`;
const Name = styled.span`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Figure = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Side = styled.span`
  ${newLookType.label};
  font-size: 13px;
  font-weight: 500;
`;
const Meta = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Muted = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Unit = styled.span`
  margin-left: 4px;
  font-size: 12px;
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin: -3px 0 -3px 4px;
  padding: 0;
  border: 0;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: none;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  cursor: pointer;
  &:hover {
    color: ${(p) => getNewLookTokens(p.theme).accent};
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
  }
`;
